import { useEffect } from "react";
import { DropDown } from "../Common/dropDown";
import { InputField } from "../Common/inputField";
import { DatePickerInput } from "../Common/datePicker";
import TrashIcon from "../../assets/trash-outline.svg";
import { CurrencyInputField } from "../Common/currencyInput";
import { useAddressLookupStore } from "../../stores/addressLookupStore";
import { formatCanadaPostalCode } from "../../utils/postalCodeFormatter";
import { AddressLookupInput } from "../Common/AddressLookup/addressLookpInput";
import {
  CanadianProvinceCodeEnum,
  PropertyTypeMapping,
} from "../../interfaces/mortgageDetailsInterface";

interface RentalIncomeBoxProps {
  componentId: string;
  onRemove: () => void;
  address: string;
  address2: string;
  city: string;
  postalCode: string;
  province: string;
  propertyType: string;
  propertyValue: string;
  monthlyRent: string;
  rentalIncomeSince: Date | null;
  annualAmount: string;

  addressError?: string;
  cityError?: string;
  postalCodeError?: string;
  provinceError?: string;
  propertyTypeError?: string;
  propertyValueError?: string;
  monthlyRentError?: string;
  rentalIncomeSinceError?: string;
  annualAmountError?: string;

  setAddress: (value: string) => void;
  setAddress2: (value: string) => void;
  setCity: (value: string) => void;
  setPostalCode: (value: string) => void;
  setProvince: (value: string) => void;
  setPropertyType: (value: string) => void;
  setPropertyValue: (value: string) => void;
  setMonthlyRent: (value: string) => void;
  setRentalIncomeSince: (date: Date | null) => void;
  setAnnualAmount: (value: string) => void;
}

const RentalIncomeBox = ({
  componentId,
  onRemove,
  address,
  addressError,
  setAddress,
  address2,
  setAddress2,
  annualAmount,
  annualAmountError,
  setAnnualAmount,
  city,
  cityError,
  setCity,
  postalCode,
  postalCodeError,
  setPostalCode,
  province,
  provinceError,
  setProvince,
  propertyType,
  propertyTypeError,
  setPropertyType,
  propertyValue,
  propertyValueError,
  setPropertyValue,
  monthlyRent,
  monthlyRentError,
  setMonthlyRent,
  rentalIncomeSince,
  rentalIncomeSinceError,
  setRentalIncomeSince,
}: RentalIncomeBoxProps) => {
  const { formattedAddresses } = useAddressLookupStore();
  const propertyTypeOptions = Object.keys(PropertyTypeMapping);

  const provinceOptions = Object.values(CanadianProvinceCodeEnum);

  useEffect(() => {
    const formattedAddress = formattedAddresses[componentId];
    if (formattedAddress) {
      if (address !== formattedAddress.address1) {
        setAddress(formattedAddress.address1);
      }
      if (address2 !== formattedAddress.address2) {
        setAddress2(formattedAddress.address2 || "");
      }
      if (city !== formattedAddress.city) {
        setCity(formattedAddress.city);
      }
      if (postalCode !== formattedAddress.postalCode) {
        setPostalCode(formattedAddress.postalCode);
      }
      if (province !== formattedAddress.state) {
        setProvince(formattedAddress.state);
      }
    }
  }, [
    formattedAddresses,
    componentId,
    address,
    address2,
    city,
    postalCode,
    province,
    setAddress,
    setAddress2,
    setCity,
    setPostalCode,
    setProvince,
  ]);

  return (
    <div className="rounded-[8px] border-[#E5E7EB] border-2">
      <div className="font-medium flex items-center justify-between w-full bg-[#E5E7EB]/50 rounded-t-[8px] border-b-[#E5E7EB] border-b-2 px-3 py-4">
        <div>Rental Income</div>
        <button
          type="button"
          className="flex items-center gap-1 text-[#EF4444] text-xs font-medium"
          onClick={onRemove}
        >
          Delete <img src={TrashIcon} alt="Delete icon" />
        </button>
      </div>
      <div className="p-4 space-y-6">
        <div className="space-y-2">
          <div className="text-z-dark-moss-green font-medium">
            Employment Details
          </div>
          <div className="w-full flex flex-col gap-4">
            <div className="w-full flex flex-col gap-4 lg:flex-row">
              <div className="w-full space-y-1">
                <DropDown
                  label="Property Type*"
                  options={propertyTypeOptions}
                  selectedOption={
                    Object.keys(PropertyTypeMapping).find(
                      (key) => PropertyTypeMapping[key] === propertyType
                    ) || ""
                  }
                  onSelect={(value) =>
                    setPropertyType(PropertyTypeMapping[value])
                  }
                />
                {propertyTypeError && (
                  <p className="text-red-600 text-sm">{propertyTypeError}</p>
                )}
              </div>
              <div className="w-full space-y-1">
                <CurrencyInputField
                  id="annualAmount"
                  label="Annual Amount*"
                  value={annualAmount}
                  onChange={setAnnualAmount}
                />
                {annualAmountError && (
                  <p className="text-red-600 text-sm">{annualAmountError}</p>
                )}
              </div>
            </div>
            <div>
              <AddressLookupInput type={componentId} />
            </div>
            <div className="grid lg:grid-cols-2 gap-4">
              <div className="w-full space-y-1">
                <InputField
                  id="address"
                  label="Address*"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                />
                {addressError && (
                  <p className="text-red-600 text-sm">{addressError}</p>
                )}
              </div>
              <InputField
                id="address2"
                label="Address Line 2"
                value={address2}
                onChange={(e) => setAddress2(e.target.value)}
              />
              <div className="w-full space-y-1">
                <InputField
                  id="city"
                  label="City*"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                />
                {cityError && (
                  <p className="text-red-600 text-sm">{cityError}</p>
                )}
              </div>
              <div className="flex flex-col lg:flex-row gap-4">
                <div className="w-full space-y-1">
                  <InputField
                    id="postalCode"
                    label="Postal Code*"
                    value={formatCanadaPostalCode(postalCode)}
                    onChange={(e) => setPostalCode(e.target.value)}
                  />
                  {postalCodeError && (
                    <p className="text-red-600 text-sm">{postalCodeError}</p>
                  )}
                </div>
                <div className="w-full space-y-1">
                  <DropDown
                    label="Province*"
                    options={provinceOptions}
                    selectedOption={province}
                    onSelect={setProvince}
                  />
                  {provinceError && (
                    <p className="text-red-600 text-sm">{provinceError}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="space-y-2">
          <div className="text-z-prussian-blue font-medium">
            Employment Details
          </div>
          <div className="grid lg:grid-cols-2 gap-4">
            <div className="w-full space-y-1">
              <CurrencyInputField
                id="propertyValue"
                label="Property Value ($)*"
                value={propertyValue}
                onChange={setPropertyValue}
              />
              {propertyValueError && (
                <p className="text-red-600 text-sm">{propertyValueError}</p>
              )}
            </div>
            <div className="w-full space-y-1">
              <CurrencyInputField
                id="monthlyRent"
                label="Monthly Rent ($)*"
                value={monthlyRent}
                onChange={setMonthlyRent}
              />
              {monthlyRentError && (
                <p className="text-red-600 text-sm">{monthlyRentError}</p>
              )}
            </div>
            <div className="w-full space-y-1">
              <DatePickerInput
                label="Rental Income Since*"
                selectedDate={rentalIncomeSince}
                onDateChange={setRentalIncomeSince}
                disableFuture={true}
              />
              {rentalIncomeSinceError && (
                <p className="text-red-600 text-sm">{rentalIncomeSinceError}</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export { RentalIncomeBox };
